import React, { useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { has, get, isEmpty, pick } from 'lodash'
import { DateTime } from 'luxon'
import accounting from 'accounting'
import { decode } from 'he'
import { rebuild } from 'react-tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { DateTimeDisplay, PageDescription } from '../../../../components'
import Table from '../../../../components/Table/next'
import ReportActions from '../../../../components/ReportActions'

import { SalesTableContext } from './index'
import {
  exportDetailReport,
  getDetailReport,
} from '../../../../actions/detailReport'
import { State } from '../../../../reducers'
import { pruneObject } from '../../../../utils/objectManagement'
import { getDefinition } from '../../../../utils/dictionary'

import { ReportStatus } from '../../../../typings/report'
import { SaleDetail } from '../../../../typings/detailReport'
import { DetailReportStore } from '../../../../typings/stores/DetailReportStore'

import styles from '../styles.module.css'

const SalesTable = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const {
    filters,
    pagination: { size, page },
    sort,
    changePageSize,
    changePage,
    changeSort,
  } = useContext(SalesTableContext)

  const report = useSelector<State, DetailReportStore>(
    state => state.detailReport
  )

  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.detailReport.export, ['hasData', 'status', 'error']),
    loading: state.detailReport.export.status === 'fetching',
    hasError: !isEmpty(state.detailReport.export.error),
  }))

  useEffect(() => {
    dispatch(
      getDetailReport('sales', { ...filters, ...pruneObject(sort), size, page })
    )
  }, [sort, page, size])

  useEffect(() => {
    rebuild()
  })

  const backUrl = has(location, 'state.performanceReportUrl')
    ? location.state.performanceReportUrl
    : '/app/reports'

  return (
    <>
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <Link to={backUrl} className={styles.backLink}>
            Back to Performance Report
          </Link>
          <h2>Sales Details</h2>
        </div>
        <ReportActions
          className={styles.export}
          exportStatus={exportStatus}
          reportStatus={exportStatus}
          fetchExport={() =>
            dispatch(
              exportDetailReport('sales', { ...filters, ...pruneObject(sort) })
            )
          }
        />
      </div>

      <PageDescription content="This report contains transactional sale details for the data selected in the performance report." />

      <Table<SaleDetail[]>
        className={styles.table}
        cols={[
          {
            text: 'Sale ID',
            field: 'saleId',
          },
          {
            text: 'Click ID',
            field: 'clickId',
          },
          {
            text: 'Click Date',
            sortable: true,
            field: 'clickDate',
            render: row =>
              row.clickDate && (
                <DateTimeDisplay datetime={DateTime.fromSQL(row.clickDate)} />
              ),
          },
          {
            text: 'Provider Process Date',
            sortable: true,
            field: 'processDate',
            render: row =>
              row.processDate && (
                <DateTimeDisplay datetime={DateTime.fromSQL(row.processDate)} />
              ),
          },
          {
            text: 'Product',
            sortable: true,
            field: 'productName',
            render: row => row.productName && decode(row.productName),
          },
          {
            text: 'Advertiser',
            sortable: true,
            field: 'advertiserName',
          },
          {
            text: 'Product Type',
            field: 'productType',
          },
          {
            text: 'Subtracking',
            sortable: true,
            field: 'subtracking',
          },
          {
            text: 'Commission',
            sortable: true,
            field: 'commission',
            className: styles.right,
            render: row => accounting.formatMoney(row.commission, '$'),
          },
          {
            text: (
              <div
                data-tip={getDefinition('cross sale')}
                data-for="global-tooltip"
              >
                <FontAwesomeIcon
                  icon={['fas', 'expand-arrows-alt']}
                  className={styles.icon}
                />
              </div>
            ),
            sortable: true,
            field: 'isCrossSale',
            className: styles.crossSale,
            render: row =>
              row.isCrossSale && (
                <div
                  data-tip={getDefinition('cross sale')}
                  data-for="global-tooltip"
                >
                  <FontAwesomeIcon
                    icon={['fas', 'expand-arrows-alt']}
                    className={styles.icon}
                  />
                </div>
              ),
          },
        ]}
        data={has(report, 'data.sales') && report.data.sales}
        isLoading={report.status === 'fetching'}
        onSortChange={changeSort}
        sort={sort}
        pagination={{
          total: get(report, 'data.pagination.total'),
          pageSize: get(report, 'data.pagination.pageSize'),
          currentPage: get(report, 'data.pagination.currentPage'),
          onPageSizeUpdate: changePageSize,
          onPaginate: changePage,
        }}
      />
    </>
  )
}

export default SalesTable
